import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { DataDictionaryApi } from '../../../api/data-dictionary-api';
import { util } from '../../../classes/util';
import { InputConverter } from '../../../inputConverter';
import { FilterService } from '../../../services/filter.service';
import { LayerService } from '../../../services/layer.service';
import { ToSqlStringService } from '../../../services/to-sql-string.service';
import { ILayer, ILayerColumn, ILayerRMS, IMapcoreMapImageLayer, IOperatorOption, IQuery, isQueryableMapCoreLayer, ITable, ITableColumn, IWorkingQuery } from '../../../types';
import { BaseComponent } from '../../base.component';

const shortWait = 10;
const joinTableAlias = 'JOINTABLE';
const baseTableAlias = 'TBL';

/** Join Filter Component */
@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-join-filter-ng',
    styleUrls: ['../../sharedStyles/base.scss', './join-filter.component.scss', '../../sharedStyles/telerik.scss'],
    templateUrl: './join-filter.component.html'
})
export class JoinFilterComponent extends BaseComponent implements OnDestroy {
    @Input() esriLayers: NonNullable<IMapcoreMapImageLayer[]>;
    _layerId: number;
    workingSql: string;
    tableColumns: ITableColumn[] = [];
    tableColumnsDisplay: any[] = [];
    operatorOptions: IOperatorOption[];
    disableSelectMenu: boolean;
    disableObj = false;

    get layerId() { return this._layerId; }
    @Input() set layerId(layerId: number) {
        this.setLayer(layerId);
    }
    @Input()
    esriLayerId: string;
    _query: IQuery;
    get query() {
        return this._query;
    }
    @Input() set query(inputQuery: IQuery) {
        this._query = inputQuery;
        this.execQuery();
    }

    _layerRef: ILayer;
    @Input() get layerRef() {
        return this._layerRef;
    }
    set layerRef(value: ILayer) {
        this._layerRef = value;
    }
    @Input() async updateWorkingSql(summaryString) {
        this.summaryItemsString = summaryString;
        await this.populateWorkingSql();
        this.changeDetectorRef.detectChanges();
    }
    @Input() updateQuery = (query) => {
        this._outsideUpdate = true;
        this._query = query;
    }
    private _outsideUpdate = false;
    table: ITable;
    layerQuery: IQuery;
    layerColumns: ILayerColumn[] = [];
    selectedTable: ITable;
    _summaryItemsString = '';
    hideExport = false;
    @Input() set summaryItemsString(inputSummary: string) {
        this._summaryItemsString = inputSummary;
    }
    get summaryItemsString() {
        return this._summaryItemsString;
    }
    showSQL: boolean;
    _primaryLayerRMSKeys: ILayerRMS;
    @Input() set primaryLayerRMSKeys(inputKeys: ILayerRMS) {
        this._primaryLayerRMSKeys = inputKeys;
    }
    get primaryLayerRMSKeys() {
        return this._primaryLayerRMSKeys;
    }

    @Input() mapView: NonNullable<__esri.MapView>;
    @Input() selectedJoinRecord: any = null;
    _workingQuery: IWorkingQuery = this.filterService.getEmptyQuery();
    @Input() set workingQuery(inputQuery: IWorkingQuery) {
        this._workingQuery = inputQuery;
    }
    get workingQuery() {
        return this._workingQuery;
    }
    @Input() exportEnabled = false;
    @Input() exportJoinColumns: any[];
    populateLayerDataPromise: Promise<any>;

    @Input() finalizeQuery = () => {
        this.query = this.filterService.getFinalizedQuery(this.workingQuery);
    }

    @Input() set hideExportSection(value) {
        this.hideExport = value;
    }
    get hideExportSection() {
        return this.hideExport;
    }

    @Input()
    @InputConverter()
    updateLayerOnApply: boolean = true;

    @Output() public readonly ondone = new EventEmitter<{ layerId: number; sql: string; query: IQuery }>();
    @Output() public readonly workingQueryChange = new EventEmitter<{ workingQuery: IWorkingQuery; joinColumns: ITableColumn[] }>();
    @Output() public readonly exportOptionChange = new EventEmitter<{ exportEnabled: boolean; layerColumns: ILayerColumn[]; exportJoinColumns: any[] }>();
    @Output() public readonly onapply = new EventEmitter<{ id?: string; esriLayer?: any; data?: any }>();
    joinRecords: any[];

    constructor(
        _vcr: ViewContainerRef,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly layerService: LayerService,
        private readonly filterService: FilterService,
        private readonly toSqlStringService: ToSqlStringService,
        private readonly dataDictionaryApi: DataDictionaryApi) {
        super(_vcr);
        this.registerEvent('ondone', this.ondone);
        this.registerEvent('onapply', this.onapply);
        this.registerEvent('workingQueryChange', this.workingQueryChange);
        this.registerEvent('exportOptionChange', this.exportOptionChange);
        this.setOperatorOptions();
    }

    get andOr(): string {
        return this.workingQuery.joinTableQuery.operator === 'OR' ? 'or' : 'and';
    }

    get showFilter() {
        return this.layerColumns.length > 0;
    }

    ngOnDestroy() {
        this.reset();
    }

    reset() {
        this.selectedJoinRecord = null;
        this.joinRecords = [];
        this.exportEnabled = false;
    }

    async execQuery() {
        if (this.populateLayerDataPromise) {
            await this.populateLayerDataPromise;
        }
        await this.refreshSubqueries();
    }
    async populateJoinRecords() {
        this.joinRecords = await this.layerService.getTableRMSKeys();
        if (this.query && this.query.joinRecord && this.query.joinRecord.tableId && this.query.joinRecord.tableId > 0) {
            this.selectedJoinRecord = this.joinRecords.filter(x => x.tableId === this.query.joinRecord.tableId)[0];
            await this.populateSelectedJoinedTable(this.selectedJoinRecord.tableId);
        }
    }
    async setLayer(layerId: number) {
        this._layerId = layerId;
        this.workingSql = '';
        this.layerRef = undefined;
        this.table = null;
        this.layerColumns.length = 0;
        this.tableColumnsDisplay.length = 0;
        await this.populateLayerData();
        this.populateLayerDataPromise = this.populateJoinRecords();
        await this.populateLayerDataPromise;
        this.populateWorkingQueryFromQuery();
        this.isJoinRecordSelected();
        await this.refreshSubqueries();
        this.setExportColumns();
        this.changeDetectorRef.detectChanges();
    }

    resetSubQueries() {
        this.workingQuery.joinTableQuery.subqueries = [{ subqueries: [], operator: 'AND' }];
        this.filterService.clearValuesIfNeeded(this.workingQuery.joinTableQuery.subqueries);
        this.workingQuery.joinTableQuery.subqueries = this.filterService.populateWorkingSubqueryDisplayProperties(this.workingQuery.joinTableQuery.subqueries, this.layerColumns);
    }

    remove(i: number) {
        this.workingQuery.joinTableQuery.subqueries.splice(i, 1);
        this.refreshSubqueries();
    }

    setAll() {
        this.workingQuery.joinTableQuery.operator = 'AND';
        this.refreshSubqueries();
    }

    setAny() {
        this.workingQuery.joinTableQuery.operator = 'OR';
        this.refreshSubqueries();
    }
    setOperatorOptions() {
        this.operatorOptions = this.toSqlStringService.getOperatorOptions();
    }

    async joinRecordChanged() {
        if (typeof this.selectedJoinRecord === 'string') {
            this.selectedJoinRecord = null;
        }
        if (this.selectedJoinRecord) {
            const tableId = this.selectedJoinRecord.tableId;
            await this.populateSelectedJoinedTable(tableId);
        }
        this.resetExportColumns();
        this.workingQuery.joinRecord = this.selectedJoinRecord;
        this.workingQuery.primaryLayerRMSKeys = this.primaryLayerRMSKeys;
        this.resetSubQueries();
        this.isJoinRecordSelected();
        await this.populateWorkingSql();
        this.changeDetectorRef.detectChanges();
        this.emitWorkingQuery();
    }
    async populateSelectedJoinedTable(tableId: number) {
        this.selectedTable = await this.layerService.getTableColumns(tableId);
        this.tableColumns = this.selectedTable.columns;
        this.tableColumnsDisplay = this.tableColumns.sort((a, b) => (a.name > b.name) ? 1 : -1);
        this.selectedJoinRecord.isOffsetNumber = this.filterService.offSetIsNumber(this.selectedJoinRecord.offsetEndColName, this.tableColumns);
        if (this.selectedTable && this.selectedTable.metaTable && this.tableColumns) {
            const dataDictionaryColumns = await this.dataDictionaryApi.getColumnsWithDomainValuesByMetaTableId(this.selectedTable.metaTable.id);
            if (dataDictionaryColumns) {
                for (const column of this.tableColumns) {
                    const matchingColumn = dataDictionaryColumns.find(c => c.columnName.toLowerCase().trim() === column.name.toLowerCase().trim());
                    if (matchingColumn && matchingColumn.domainValues && matchingColumn.domainValues.length) {
                        column.domainValues = matchingColumn.domainValues;
                    }
                }
            }
        }
    }
    applyFilterChangeToLayer() {
        this.ondone.emit({ layerId: this.layerId, sql: this.workingSql, query: this.query});
    }
    addBottomSubqueryIfNeeded() {
        if (this.workingQuery.joinTableQuery.subqueries) {
            let addWorkingSubquery: boolean = this.workingQuery.joinTableQuery.subqueries.length === 0;
            if (!addWorkingSubquery) {
                const lastSubquery = this.workingQuery.joinTableQuery.subqueries[this.workingQuery.joinTableQuery.subqueries.length - 1];
                addWorkingSubquery = this.toSqlStringService.queryConditionIsValid(lastSubquery);
            }
            if (addWorkingSubquery) {
                this.workingQuery.joinTableQuery.subqueries.push({ field: '', operator: '=', value: null});
            }
        }
    }

    async populateLayerData() {
        if (this.layerId) {
            const layers = await this.filterService.getLayerData(this.layerId);
            if (layers.length > 0) {
                this.layerRef = layers[0];
                const tables = this.layerRef.tables;
                this.table = tables.sort((a, b) => a.geometryType.localeCompare(b.geometryType))[0];
                if (this.layerRef.queryJSON) {
                    this.layerQuery = JSON.parse(this.layerRef.queryJSON);
                } else {
                    this.layerQuery = {};
                }
                this.layerColumns.length = 0;
                this.layerColumns.push(...layers[0].columns);
                for (const layerColumn of this.layerColumns) {
                    layerColumn.alias = layerColumn.alias.toLowerCase();
                }
                return this.layerColumns;
            }
        }
        return [];
    }

    populateWorkingQueryFromQuery() {
        if (this.query) {
            this.workingQuery = this.filterService.populateWorkingQueryFromQuery(this.query, this.tableColumns);
            this.workingQuery.primaryLayerRMSKeys = this.primaryLayerRMSKeys;
        }
    }
    async populateWorkingSql() {
        if (this.table) {
            this.workingSql = await this.filterService.populateWorkingSql(this.layerQuery, this.workingQuery, this.table, this.layerColumns);
        }
    }

    refreshSubqueries: () => void = util.debounce(async () => {
        await this.refreshSubqueriesWork();
    }, shortWait);

    private async refreshSubqueriesWork() {
        this.filterService.clearValuesIfNeeded(this.workingQuery.joinTableQuery.subqueries);
        this.workingQuery.joinTableQuery.subqueries = this.filterService.populateWorkingSubqueryDisplayProperties(this.workingQuery.joinTableQuery.subqueries, this.layerColumns);
        await this.populateWorkingSql();
        if (this.selectedJoinRecord && this.workingQuery.joinTableQuery.subqueries && this.workingQuery.joinTableQuery.subqueries.length) {
            const populatePromises: Promise<any>[] = [];
            this.workingQuery.joinTableQuery.subqueries.forEach((sq, i) => {
                if (!sq.distinctValues) {
                    populatePromises.push(this.filterService.populateDistinctValues(this.selectedJoinRecord.tableId, sq, this.layerQuery.joinTableQuery, this.workingQuery.joinTableQuery, this.tableColumns, i, true, this.esriLayers && this.esriLayers.length ? this.esriLayers[0].mapcoreProps.isPETL : null));
                }
            });
            await Promise.all(populatePromises);
        }
        this.addBottomSubqueryIfNeeded();
        this.showSelectMenu();
        this.changeDetectorRef.detectChanges();
        this.emitWorkingQuery();
    }

    toggleShowSql() {
        this.showSQL = !this.showSQL;
    }

    emitWorkingQuery() {
        if (!this._outsideUpdate) {
            this.workingQueryChange.emit({ workingQuery: this.workingQuery, joinColumns: this.tableColumns });
        }
        this._outsideUpdate = false;
        this.changeDetectorRef.detectChanges();
    }

    showSelectMenu() {
        let addWorkingSubquery = false;
        if (this.workingQuery && this.workingQuery.joinTableQuery && this.workingQuery.joinTableQuery.subqueries) {
            addWorkingSubquery = this.workingQuery.joinTableQuery.subqueries.length === 0;
            if (!addWorkingSubquery) {
                const lastSubquery = this.workingQuery.joinTableQuery.subqueries[0];
                addWorkingSubquery = this.toSqlStringService.queryConditionIsValid(lastSubquery);
            }
        }
        this.disableSelectMenu =  addWorkingSubquery;
    }

    layerData(layerRef) {
        const mcWindow = document.getElementById('mcWindow');
        let titleText = 'Filter';
        if (layerRef) {
            titleText = `Filter: ${layerRef.name}`;
        }
        mcWindow.title = titleText;
    }
    handleFilter(value, isColumnName, subquery?: IWorkingQuery) {
        if (isColumnName) {
            this.tableColumnsDisplay = this.tableColumns.filter((s) => util.LooseIncludes(s.name, value) || (s.alias && util.LooseIncludes(s.alias, value)));
        } else if (value.length > 1 && subquery.distinctValues) {
            subquery.filteredDistinctValues = subquery.distinctValues.filter(v => util.LooseIncludes(v.text, value) || util.LooseIncludes(v.value, value));
        } else {
            subquery.filteredDistinctValues = subquery.distinctValues;
        }
        this.changeDetectorRef.detectChanges();
    }

    valuechange() {
        this.changeDetectorRef.detectChanges();
        setTimeout(() => { this.refreshSubqueries(); }, shortWait);
    }
    setExportColumns() {
        this.exportOptionChange.emit({
            exportEnabled: this.exportEnabled,
            layerColumns: this.layerColumns,
            exportJoinColumns: this.exportJoinColumns
        });
        this.changeDetectorRef.detectChanges();
    }
    setAllExportColumns() {
        this.exportJoinColumns = this.tableColumnsDisplay;
        this.changeDetectorRef.detectChanges();
    }
    resetExportColumns() {
        this.exportJoinColumns = [];
        this.changeDetectorRef.detectChanges();
    }
    async selectedFilterColumn(subquery: IWorkingQuery, subqueryIndex) {
        subquery.multiValue = null;
        subquery.value = null;
        this.changeDetectorRef.detectChanges();
        subquery = await this.filterService.populateDistinctValues(this.selectedJoinRecord.tableId, subquery, this.layerQuery, this.workingQuery, this.tableColumns, subqueryIndex, true, this.esriLayers && this.esriLayers.length ? this.esriLayers[0].mapcoreProps.isPETL : null);
        this.refreshSubqueries();
    }

    async applyFilter() {
        this.finalizeQuery();
        const data = {
            layerId: this.layerId,
            sql: this.workingSql,
            query: this.query,
            joinColumns: null,
            exportJoinFilterParams: null,
            exportEnabled: undefined,
            exportJoinColumns: null
        };
        this.ondone.emit(data);

        if (this.updateLayerOnApply) {
            data.joinColumns = this.tableColumns;
            data.exportJoinFilterParams = { exportEnabled: this.exportEnabled };
            if (this.exportEnabled) {
                data.exportEnabled = this.exportEnabled;
                data.exportJoinColumns = this.exportJoinColumns;
            }
            this.onapply.emit({ id: data.layerId.toString(), data });

            const esriLayer = this.layerService.getEsriLayerByUniqueId(this.esriLayerId);
            if (esriLayer && data && isQueryableMapCoreLayer(esriLayer)) {
                esriLayer.mapcoreProps.filter = data.sql;
                esriLayer.mapcoreProps.query = data.query;
                esriLayer.mapcoreProps.joinColumns = data.joinColumns;
                esriLayer.mapcoreProps.exportJoinFilterParams = { exportEnabled: this.exportEnabled };
                if (esriLayer.mapcoreProps.exportJoinFilterParams) {
                    esriLayer.mapcoreProps.exportJoinFilterParams.exportEnabled = this.exportEnabled;
                    esriLayer.mapcoreProps.exportJoinFilterParams.exportJoinColumns = this.exportJoinColumns;
                }
                this.layerService.updateLayerFilter(esriLayer, data.query);
                this.onapply.emit({ esriLayer });
            } else {
                this.onapply.emit(null);
            }
        }

    }

    exportButtonChanged(e) {
        this.exportEnabled = e.target.checked;
        if (!this.exportEnabled) {
            this.exportJoinColumns = [];
        }
        this.setExportColumns();
    }
    buildExportJoinQuery() {
        let sql = '';
        const combinedColumns: string[] = [];
        this.layerColumns.forEach(function(column) {
            combinedColumns.push(`${baseTableAlias}.${column.name} as ${column.name}`);
        });
        this.exportJoinColumns.forEach(function(column) {
            combinedColumns.push(`${joinTableAlias}.${column.name} as ${column.name}_1`);
        });

        if (this.workingQuery.subqueries.values !== null) {
            sql = `(Select * from ${this.table.owner}.${this.table.name} Where ${this.toSqlStringService.getSql(this.workingQuery)})`;
        }

        let sqlQuery = `Select ${combinedColumns.join()} from ${sql} ${baseTableAlias}`;
        const joinSql = this.toSqlStringService.getJoinFilter(this.query, baseTableAlias, joinTableAlias);
        sqlQuery += joinSql;

        return sqlQuery;
    }
    isJoinRecordSelected() {
         this.disableObj = (typeof this.selectedJoinRecord === 'string') || this.selectedJoinRecord === null || this.selectedJoinRecord === undefined;
    }
    cancel() {
        this.ondone.emit(null);
    }

    @Input()
    setFilter(layerId: number, esriLayerId: string, layerColumns: ILayerColumn[], query: IQuery) {
        this._layerId = layerId;
        this.esriLayerId = esriLayerId;
        this.layerColumns = layerColumns;
        this._query = query;
        this._outsideUpdate = true;
        this.setLayer(layerId);
    }
}
